import React, { useState, useEffect, useCallback } from 'react';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { fetchMyListings } from '../api/marketplaceApi';

const SupplierInventory: React.FC = () => {
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchMyListings();
      setListings(response.data?.items || response.data || response.items || response || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n: number) => new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', minimumFractionDigits: 0 }).format(n || 0);

  const totalStock = listings.reduce((sum: number, l: any) => sum + (l.available_quantity || l.availableQuantity || 0), 0);
  const totalReserved = listings.reduce((sum: number, l: any) => sum + (l.reservation_quantity || l.reservationQuantity || 0), 0);
  const activeListings = listings.filter((l: any) => l.status === 'active' || l.status === 'published').length;
  const lowStock = listings.filter((l: any) => (l.available_quantity || l.availableQuantity || 0) < 10 && (l.available_quantity || l.availableQuantity || 0) > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <ArchiveBoxIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Inventory</h1>
              <p className="text-blue-100 mt-1">Track stock levels across your listings</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Stock</p>
          <p className="text-3xl font-extrabold text-gray-900 mt-2">{totalStock}</p>
          <p className="text-xs text-gray-400 mt-1">units available</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Reserved</p>
          <p className="text-3xl font-extrabold text-amber-600 mt-2">{totalReserved}</p>
          <p className="text-xs text-gray-400 mt-1">units locked</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Active Listings</p>
          <p className="text-3xl font-extrabold text-emerald-600 mt-2">{activeListings}</p>
          <p className="text-xs text-gray-400 mt-1">published</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Low Stock</p>
          <p className="text-3xl font-extrabold text-red-600 mt-2">{lowStock.length}</p>
          <p className="text-xs text-gray-400 mt-1">listings need restock</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-blue-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : listings.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto">
            <ArchiveBoxIcon className="w-10 h-10 text-blue-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">No inventory yet</h3>
          <p className="text-gray-500 mt-2">Create listings to start managing your inventory</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Available</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Reserved</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stock Level</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listings.map((l: any) => {
                  const available = l.available_quantity || l.availableQuantity || 0;
                  const reserved = l.reservation_quantity || l.reservationQuantity || 0;
                  const total = available + reserved;
                  const pct = total > 0 ? Math.round((available / total) * 100) : 0;
                  const isLow = available < 10 && available > 0;

                  return (
                    <tr key={l.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900">{l.title || l.product_name || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{l.unit || 'kg'}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{l.category || l.catalog_category || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900 text-right">{fmt(l.price_per_unit || l.pricePerUnit || 0)}</td>
                      <td className="px-6 py-4 text-right">
                        <span className={`text-lg font-extrabold ${isLow ? 'text-red-600' : 'text-gray-900'}`}>{available}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-semibold text-amber-600">{reserved}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-32">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-500">{pct}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                          l.status === 'active' || l.status === 'published'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : l.status === 'paused'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}>
                          {l.status || 'draft'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierInventory;
